import { useEffect, useState } from "react";
import { X } from "lucide-react";
import api from "../../lib/api";
import { useApp } from "../../context/AppContext";
import SEO from "../../components/SEO";
import { PageHero, EmptyState, LazyImg } from "../../components/Bits";

export default function Gallery() {
  const { t, lang } = useApp();
  const [items, setItems] = useState([]);
  const [albums, setAlbums] = useState([]);
  const [category, setCategory] = useState("");
  const [album, setAlbum] = useState("");
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams();
    if (album) params.set("album", album);
    else if (category) params.set("category", category);
    api.get(`/public/gallery${params.toString() ? `?${params}` : ""}`).then((r) => setItems(r.data)).catch(() => {});
  }, [category, album]);

  useEffect(() => {
    api.get("/public/gallery-albums").then((r) => setAlbums(r.data)).catch(() => {});
  }, []);

  const categories = [...new Set(items.map((i) => i.category).filter(Boolean))];

  return (
    <div data-testid="gallery-page">
      <SEO title={t("gallery")} />
      <PageHero title={t("gallery")} sub={lang === "hi" ? "हमारे कार्यक्रमों और गतिविधियों की झलकियाँ।" : "Moments from our programmes and community activities."} />
      <section className="py-16">
        <div className="container-ssf">
          {albums.length > 0 && (
            <div className="mb-4 flex flex-wrap gap-2" data-testid="album-chips">
              <button onClick={() => { setAlbum(""); setCategory(""); }} data-testid="album-filter-all"
                className={`rounded-full px-5 py-2 text-sm font-semibold transition-colors ${!album && !category ? "bg-navy text-white" : "bg-white text-navy border border-gray-200"}`}>
                {lang === "hi" ? "सभी" : "All"}
              </button>
              {albums.map((a) => (
                <button key={a.id} onClick={() => { setAlbum(a.id); setCategory(""); }} data-testid={`album-filter-${a.id}`}
                  className={`rounded-full px-5 py-2 text-sm font-semibold transition-colors ${album === a.id ? "bg-navy text-white" : "bg-white text-navy border border-gray-200"}`}>
                  {lang === "hi" && a.title_hi ? a.title_hi : a.title_en}
                </button>
              ))}
            </div>
          )}
          {!album && categories.length > 0 && (
            <div className="mb-8 flex flex-wrap gap-2">
              {categories.map((c) => (
                <button key={c} onClick={() => setCategory(c === category ? "" : c)} data-testid={`gallery-filter-${c}`}
                  className={`rounded-full px-4 py-1.5 text-xs font-semibold transition-colors ${category === c ? "bg-saffron-500 text-navy" : "bg-white text-slate-500 border border-gray-200"}`}>
                  {c}
                </button>
              ))}
            </div>
          )}
          {album && albums.find((a) => a.id === album)?.description && (
            <p className="mb-6 text-sm text-slate-500" data-testid="album-description">{albums.find((a) => a.id === album).description}</p>
          )}
          {items.length === 0 && <EmptyState text="Photos will appear here once published." />}
          <div className="columns-2 gap-4 md:columns-3 lg:columns-4 [&>*]:mb-4">
            {items.map((g) => (
              <button key={g.id} onClick={() => setLightbox(g)} data-testid={`gallery-item-${g.id}`}
                className="group relative block w-full overflow-hidden rounded-xl text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-saffron-500">
                <LazyImg src={g.thumb || g.image} alt={g.alt_text || g.heading || g.caption || "Gallery image"} className="w-full object-cover transition-transform duration-300 group-hover:scale-105" />
                {(g.heading || g.caption) && (
                  <span className="absolute inset-x-0 bottom-0 bg-navy/70 px-3 py-2 text-xs font-semibold text-white opacity-0 backdrop-blur transition-opacity group-hover:opacity-100">
                    {g.heading || g.caption}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      </section>
      {lightbox && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-navy/90 p-4 backdrop-blur" onClick={() => setLightbox(null)} role="dialog" aria-modal="true" data-testid="gallery-lightbox">
          <button className="absolute right-5 top-5 rounded-full bg-white/10 p-2 text-white hover:bg-white/20" onClick={() => setLightbox(null)} aria-label="Close" data-testid="lightbox-close"><X size={22} /></button>
          <figure className="max-w-4xl" onClick={(e) => e.stopPropagation()}>
            <img src={lightbox.image} alt={lightbox.alt_text || lightbox.heading || ""} className="max-h-[80vh] w-full rounded-xl object-contain" />
            {(lightbox.heading || lightbox.caption) && (
              <figcaption className="mt-3 text-center text-sm text-slate-200">{lightbox.heading}{lightbox.caption ? ` — ${lightbox.caption}` : ""}</figcaption>
            )}
          </figure>
        </div>
      )}
    </div>
  );
}
